import { atom } from "jotai";
import { atomFamily, atomWithStorage, createJSONStorage } from "jotai/utils";
import type { MenuIngredient } from "@/api/vendure/menu/menu.types";
import type { OrderDto } from "@/api/vendure/order/order.types";
import type { SelectedOptions } from "@/components/business/products/option.utils";
import { currentVenueSlugAtom } from "@/stores/venue.store";

export interface CartItem {
  cartItemId: string;
  productId: number;
  name: string;
  image: string | null;
  unitPrice: number;
  quantity: number;
  options: SelectedOptions;
  comment: string;
  total: number;
  removedIngredients?: RemovedIngredient[];
}

export type RemovedIngredient = Pick<MenuIngredient, "id" | "name">;

const cartItemsFamily = atomFamily((slug: string) =>
  atomWithStorage<CartItem[]>(
    `munchi-cart-${slug}`,
    [],
    createJSONStorage<CartItem[]>(() => window.sessionStorage),
  ),
);

const cartCommentFamily = atomFamily((slug: string) =>
  atomWithStorage<string>(
    `munchi-cart-comment-${slug}`,
    "",
    createJSONStorage<string>(() => window.sessionStorage),
  ),
);

export const cartItemsAtom = atom(
  (get): CartItem[] => {
    const slug = get(currentVenueSlugAtom);
    return slug ? get(cartItemsFamily(slug)) : [];
  },
  (get, set, value: CartItem[]) => {
    const slug = get(currentVenueSlugAtom);
    if (slug) set(cartItemsFamily(slug), value);
  },
);

export const cartCommentAtom = atom(
  (get): string => {
    const slug = get(currentVenueSlugAtom);
    return slug ? get(cartCommentFamily(slug)) : "";
  },
  (get, set, value: string) => {
    const slug = get(currentVenueSlugAtom);
    if (slug) set(cartCommentFamily(slug), value);
  },
);

export const cartOpenAtom = atom(false);

export const cartStateAtom = atom((get) => {
  const items = get(cartItemsAtom);
  return {
    items,
    totalItems: items.reduce((s, i) => s + i.quantity, 0),
    totalPrice: items.reduce((s, i) => s + i.total, 0),
  };
});

export const addToCartAtom = atom(null, (get, set, item: CartItem) => {
  set(cartItemsAtom, [...get(cartItemsAtom), item]);
});

export const removeFromCartAtom = atom(null, (get, set, cartItemId: string) => {
  set(
    cartItemsAtom,
    get(cartItemsAtom).filter((i) => i.cartItemId !== cartItemId),
  );
});

export const updateCartItemQtyAtom = atom(
  null,
  (get, set, cartItemId: string, qty: number) => {
    set(
      cartItemsAtom,
      get(cartItemsAtom).map((i) =>
        i.cartItemId === cartItemId
          ? {
              ...i,
              quantity: Math.max(1, qty),
              total: Math.max(1, qty) * i.unitPrice,
            }
          : i,
      ),
    );
  },
);

export const clearCartAtom = atom(null, (_, set) => {
  set(cartItemsAtom, []);
});

export const orderAtom = atom<OrderDto | null>(null);
export const cartSyncingAtom = atom(false);
export const couponPendingAtom = atom(false);

const nullableStringSessionStorage = createJSONStorage<string | null>(
  () => window.sessionStorage,
);

export const lastOrderCodeAtom = atomWithStorage<string | null>(
  "munchi-last-order",
  null,
  nullableStringSessionStorage,
);

export const lastOrderSlugAtom = atomWithStorage<string | null>(
  "munchi-last-order-slug",
  null,
  nullableStringSessionStorage,
);
